import {
  type AllKeys,
  notEmpty,
  type Optional,
  type OptionalRecord,
  type TypeForKey,
} from '@festina-life/core'
import get from 'lodash-es/get'
import { type JSX, type ReactNode, useState } from 'react'

import { IconButton } from '../../actions/iconButton/IconButton'
import { JsTreeView } from '../../listsAndTables/jsTree/JsTreeView'
import { Card } from '../card/Card'
import { CardBody } from '../card/CardBody'
import { CardHeader } from '../card/CardHeader'
import { CardItem } from '../card/CardItem'
import { CardItems } from '../card/CardItems'
import { SimpleModal } from '../modal/SimpleModal'

type Props<T extends OptionalRecord, K extends AllKeys<T>> = {
  currentVal: unknown
  prevVal: unknown
  path: string | number | undefined
  formatter: (
    value: Optional<TypeForKey<T, K>>,
  ) => Optional<string | number | JSX.Element>
  description?: string
  title?: string | ReactNode
}

export const ExtraTestInformation = <
  T extends OptionalRecord,
  K extends AllKeys<T>,
>({
  path,
  currentVal,
  prevVal,
  formatter,
  description,
  title,
}: Props<T, K>) => {
  const currentValueFromPath =
    path && currentVal ? get(currentVal, path) : currentVal
  const previousValueFromPath = path && prevVal ? get(prevVal, path) : prevVal

  const type = get(currentVal, '_type') ?? get(prevVal, '_type')

  const outputVal = (value: unknown): string | number => {
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    return (value as string)?.toString()
  }

  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        className="absolute! top-1/2 -translate-y-1/2 right-0 opacity-30 hover:opacity-100 text-[hotpink]! p-0!"
        iconName="BugAnt"
      />
      <SimpleModal
        title={title ?? 'Debug value'}
        isOpen={isOpen}
        closeConfiguration={{ onClose: () => setIsOpen(false) }}
      >
        <CardItems>
          <CardItem title="Origin" value={type} />
          <CardItem title="Path" value={path} />
          {description && <div>Description: {description}</div>}
        </CardItems>

        <Card className="mt-2">
          <CardHeader title="Current value" />
          <CardBody>
            <CardItem
              title="Raw"
              value={
                notEmpty(currentValueFromPath)
                  ? outputVal(currentValueFromPath)
                  : ''
              }
            />
            <CardItem
              title="Formatted"
              value={
                notEmpty(formatter(currentValueFromPath))
                  ? formatter(currentValueFromPath)
                  : ''
              }
            />
            {notEmpty(currentVal) && (
              <JsTreeView isInitiallyClosed data={currentVal} />
            )}
          </CardBody>
        </Card>
        {previousValueFromPath ? (
          <Card className="mt-2">
            <CardHeader title="Previous value" />
            <CardBody>
              <CardItem
                title="Raw"
                value={
                  notEmpty(previousValueFromPath) ? previousValueFromPath : ''
                }
              />
              <CardItem
                title="Formatted"
                value={
                  notEmpty(previousValueFromPath)
                    ? formatter(previousValueFromPath)
                    : ''
                }
              />

              {notEmpty(prevVal) && (
                <JsTreeView isInitiallyClosed data={prevVal} />
              )}
            </CardBody>
          </Card>
        ) : null}
      </SimpleModal>
    </>
  )
}
